import React, { useEffect, useState } from 'react';
import { api } from '../api';
import { useStrategy } from '../StrategyContext';

interface Trade {
  id: string;
  symbol: string;
  action: string;
  side: string;
  entry_price: number | null;
  exit_price: number | null;
  size_usd: number | null;
  pnl_pct: number | null;
  pnl_usd: number | null;
  hold_days: number | null;
  exit_reason: string | null;
  trading_mode: string;
  created_at: string;
}

const actionColor: Record<string, string> = {
  BUY: 'badge-green',
  SELL: 'badge-red',
  PARTIAL_TP1: 'badge-green',
  PARTIAL_TP2: 'badge-green',
  PYRAMID: 'badge-yellow',
  SHORT: 'badge-red',
  COVER: 'badge-green',
  SHORT_PARTIAL_TP1: 'badge-green',
  SHORT_PARTIAL_TP2: 'badge-green',
  FUTURES_BUY: 'badge-green',
  FUTURES_SELL: 'badge-red',
  FUTURES_PARTIAL_TP1: 'badge-green',
  FUTURES_PARTIAL_TP2: 'badge-green',
  FUTURES_PYRAMID: 'badge-yellow',
};

const COINS = ['BTC', 'ETH', 'SOL', 'XRP', 'SUI', 'LINK', 'ADA', 'NEAR', 'DOGE'];

const Trades: React.FC = () => {
  const { strategy } = useStrategy();
  const [trades, setTrades] = useState<Trade[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // Filters
  const [mode, setMode] = useState('paper');
  const [symbol, setSymbol] = useState('');
  const [action, setAction] = useState('');
  const [side, setSide] = useState('');
  const [days, setDays] = useState<number | undefined>(undefined);
  const [strategyFilter, setStrategyFilter] = useState<string>('current');

  const effectiveStrategy = strategyFilter === 'all' ? undefined : strategy;

  const fetchTrades = async () => {
    setLoading(true);
    try {
      const result = await api.getTrades({
        mode: mode || undefined,
        symbol: symbol || undefined,
        action: action || undefined,
        side: side || undefined,
        days,
        strategy: effectiveStrategy,
      });
      setTrades(result.trades || []);
      setTotal(result.total || 0);
    } catch (err) {
      console.error('Trades fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrades();
  }, [mode, symbol, action, side, days, strategy, strategyFilter]);

  const exportCsv = () => {
    if (trades.length === 0) return;
    const headers = ['Date', 'Mode', 'Side', 'Coin', 'Action', 'Entry', 'Exit', 'Size', 'P&L %', 'P&L $', 'Hold', 'Reason'];
    const rows = trades.map(t => [
      new Date(t.created_at).toLocaleDateString(),
      t.trading_mode,
      t.side || 'LONG',
      t.symbol,
      t.action,
      t.entry_price ?? '',
      t.exit_price ?? '',
      t.size_usd ?? '',
      t.pnl_pct ?? '',
      t.pnl_usd ?? '',
      t.hold_days ?? '',
      t.exit_reason ?? '',
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tradesavvy_trades_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatPrice = (p: number | null) => p != null ? `$${p.toLocaleString()}` : '—';

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Trade History</div>
        <div className="header-actions">
          <button className="btn" onClick={exportCsv}>Export CSV</button>
        </div>
      </div>

      <div className="filters-bar">
        <select className="filter-select" value={strategyFilter} onChange={e => setStrategyFilter(e.target.value)}>
          <option value="current">{strategy === 'donchian' ? 'Donchian' : 'UAlgo'}</option>
          <option value="all">All Strategies</option>
        </select>
        <select className="filter-select" value={mode} onChange={e => setMode(e.target.value)}>
          <option value="paper">Paper Trades</option>
          <option value="live">Live Trades</option>
          <option value="">All Modes</option>
        </select>
        <select className="filter-select" value={side} onChange={e => setSide(e.target.value)}>
          <option value="">All Sides</option>
          <option value="LONG">Spot (Long)</option>
          <option value="FUTURES_LONG">Futures Long</option>
          <option value="SHORT">Futures Short</option>
        </select>
        <select className="filter-select" value={symbol} onChange={e => setSymbol(e.target.value)}>
          <option value="">All Coins</option>
          {COINS.map(c => <option key={c} value={`${c}-USD`}>{c}</option>)}
        </select>
        <select className="filter-select" value={action} onChange={e => setAction(e.target.value)}>
          <option value="">All Types</option>
          <option value="BUY">BUY</option>
          <option value="SELL">SELL</option>
          <option value="PARTIAL_TP1">PARTIAL</option>
          <option value="PYRAMID">PYRAMID</option>
          <option value="SHORT">SHORT</option>
          <option value="COVER">COVER</option>
        </select>
        <select className="filter-select" value={days ?? 'all'} onChange={e => setDays(e.target.value === 'all' ? undefined : Number(e.target.value))}>
          <option value="all">All time</option>
          <option value="90">Last 90 days</option>
          <option value="30">Last 30 days</option>
        </select>
        <span style={{ marginLeft: 'auto', fontSize: 13, color: '#71717a' }}>
          {loading ? 'Loading...' : `Showing ${trades.length} of ${total} ${mode} trades`}
        </span>
      </div>

      <div className="card">
        {trades.length === 0 && !loading ? (
          <div className="card-body">
            <div className="empty-state">
              <div className="empty-state-icon">&#8644;</div>
              <div className="empty-state-text">No trades yet</div>
              <div className="empty-state-sub">Trades will appear here once the bot executes its first signal.</div>
            </div>
          </div>
        ) : (
          <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Mode</th>
                <th>Side</th>
                <th>Coin</th>
                <th>Action</th>
                <th style={{ textAlign: 'right' }}>Entry</th>
                <th style={{ textAlign: 'right' }}>Exit</th>
                <th style={{ textAlign: 'right' }}>Size</th>
                <th style={{ textAlign: 'right' }}>P&L</th>
                <th style={{ textAlign: 'right' }}>Hold</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {trades.map((t) => {
                const tradeSide = t.side || 'LONG';
                return (
                  <tr key={t.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{new Date(t.created_at).toLocaleDateString()}</td>
                    <td><span className={`badge badge-${t.trading_mode === 'paper' ? 'paper' : 'live'}`}>{t.trading_mode.toUpperCase()}</span></td>
                    <td>
                      <span className="badge" style={tradeSide === 'SHORT'
                        ? { background: 'rgba(168,85,247,0.15)', color: '#a855f7' }
                        : tradeSide === 'FUTURES_LONG'
                        ? { background: 'rgba(59,130,246,0.15)', color: '#3b82f6' }
                        : { background: 'rgba(34,197,94,0.12)', color: '#22c55e' }
                      }>
                        {tradeSide === 'SHORT' ? 'FUT SHORT' : tradeSide === 'FUTURES_LONG' ? 'FUT LONG' : 'SPOT'}
                      </span>
                    </td>
                    <td style={{ fontWeight: 600 }}>{t.symbol.replace('-USD', '')}</td>
                    <td><span className={`badge ${actionColor[t.action] || 'badge-gray'}`}>{t.action.replace(/_/g, ' ')}</span></td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatPrice(t.entry_price)}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatPrice(t.exit_price)}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{t.size_usd != null ? `$${t.size_usd.toLocaleString()}` : '—'}</td>
                    <td style={{ textAlign: 'right' }}>
                      {t.pnl_pct != null ? (
                        <span style={{ fontWeight: 600, fontSize: 14, fontVariantNumeric: 'tabular-nums' }} className={t.pnl_pct >= 0 ? 'green' : 'red'}>
                          {t.pnl_pct >= 0 ? '+' : ''}{t.pnl_pct.toFixed(1)}%
                          {t.pnl_usd != null && <><br /><span style={{ fontSize: 11 }}>{t.pnl_usd >= 0 ? '+' : ''}${t.pnl_usd.toFixed(0)}</span></>}
                        </span>
                      ) : '—'}
                    </td>
                    <td style={{ textAlign: 'right' }}>{t.hold_days != null ? `${t.hold_days}d` : '—'}</td>
                    <td style={{ color: '#71717a', fontSize: 12 }}>{t.exit_reason || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Trades;
